import React from "react";
import { MdFilterList, MdOutlineClear, MdOutlineKeyboardArrowLeft, MdOutlineKeyboardArrowRight } from "react-icons/md";
import { AiOutlineCalendar } from "react-icons/ai";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTranslation } from "react-i18next";
import EAButton from "../Buttons/EAButton";
import EABadge from "../Badge/EABadge";
import EAIconButton from "../Buttons/EAIconButton";
import "./style.scss";

interface IProps {
  hideFilters?: boolean;
  activeFilterCount?: number;
  onOpenFilters: () => void;
  onClearFilters: () => void;
  dateLabel: string;
  onPrevious: () => void;
  onNext: () => void;
  canGoPrevious?: boolean;
  canGoNext?: boolean;
  onOpenCalendar: () => void;
  previousTitle?: string;
  nextTitle?: string;
  /** Zastępuje strzałki tygodnia, np. wybór jednego dnia. */
  navigation?: React.ReactNode;
  /** Na mobile zostaje sama ikona kalendarza — dzień przełącza wtedy nagłówek siatki. */
  calendarOnlyOnMobile?: boolean;
}

const SCHEDULE_BREAKPOINT = "(max-width: 980px)";

const ScheduleBar: React.FC<IProps> = ({
  hideFilters = false,
  activeFilterCount = 0,
  onOpenFilters,
  onClearFilters,
  dateLabel,
  onPrevious,
  onNext,
  canGoPrevious = true,
  canGoNext = true,
  onOpenCalendar,
  previousTitle,
  nextTitle,
  navigation,
  calendarOnlyOnMobile = false,
}) => {
  const { t } = useTranslation();
  const isMobile = useMediaQuery(SCHEDULE_BREAKPOINT);
  const hasFilters = activeFilterCount > 0;

  const renderNavigation = () => {
    if (navigation) return navigation;
    if (isMobile && calendarOnlyOnMobile) {
      return (
        <EAIconButton
          title={t("wybierz_date", "Wybierz datę")}
          icon={<AiOutlineCalendar />}
          variant="outlined"
          compact
          onClick={onOpenCalendar}
        />
      );
    }
    return (
      <div className="schedule-bar__navigation">
        <EAIconButton
          title={previousTitle ?? (isMobile ? t("poprzedni_dzien", "Poprzedni dzień") : t("poprzedni_tydzien"))}
          icon={<MdOutlineKeyboardArrowLeft />}
          variant="outlined"
          compact={isMobile}
          onClick={onPrevious}
          disabled={!canGoPrevious}
        />
        <EAButton
          title={dateLabel}
          endIcon={<AiOutlineCalendar />}
          variant="outlined"
          compact={isMobile}
          onClick={onOpenCalendar}
        />
        <EAIconButton
          title={nextTitle ?? (isMobile ? t("nastepny_dzien", "Następny dzień") : t("nastepny_tydzien"))}
          icon={<MdOutlineKeyboardArrowRight />}
          variant="outlined"
          compact={isMobile}
          onClick={onNext}
          disabled={!canGoNext}
        />
      </div>
    );
  };

  return (
    <div className={`schedule-bar${hideFilters ? " schedule-bar--no-filters" : ""}`}>
      {!hideFilters && (
        <div className="schedule-bar__section">
          {isMobile ? (
            <>
              <EABadge value={hasFilters ? activeFilterCount : ""}>
                <EAButton
                  title={t("filtry")}
                  startIcon={<MdFilterList />}
                  variant="outlined"
                  compact
                  onClick={onOpenFilters}
                />
              </EABadge>
              {hasFilters && (
                <EAIconButton
                  title={t("wyczysc_filtry")}
                  icon={<MdOutlineClear />}
                  variant="icon"
                  compact
                  onClick={onClearFilters}
                />
              )}
            </>
          ) : (
            <>
              <EABadge value={hasFilters ? activeFilterCount : ""}>
                <EAButton title={t("filtry")} startIcon={<MdFilterList />} onClick={onOpenFilters} variant="outlined" />
              </EABadge>
              {hasFilters && (
                <EAButton
                  title={t("wyczysc_filtry")}
                  onClick={onClearFilters}
                  startIcon={<MdOutlineClear />}
                  variant="text"
                />
              )}
            </>
          )}
        </div>
      )}

      <div className="schedule-bar__section">{renderNavigation()}</div>
    </div>
  );
};

export default ScheduleBar;
